<template>
	<WaitDialog :is-active="isWaiting" title="Loading policy documents..." />
	<TraceWorksheetAnalysis :isActive="analyzerActive" :docUrl="analyzerDocUrl" @closed="analyzerClosed" />
	<v-container>
		<v-row justify="space-between" class="justify-sm-center justify-xs-center">
			<v-col class="d-flex justify-start">
				<h3>Policy Number: {{ policyNumber }}</h3>
			</v-col>
		</v-row>

		<v-tabs v-model="tab">
			<v-tab value="1">Trace Worksheets</v-tab>
		</v-tabs>

		<v-tabs-window v-model="tab">
			<v-tabs-window-item value="1">
				<v-container>
					<v-container v-if="errorMessage.length > 0" class="px-0">
						<UserNotification v-for="(error, index) in errorMessage" :key="index" :message="error.content"
							:closable="true" />
					</v-container>

					<v-data-table :headers="headers" :items="policyDocs || []" class="text-left elevation-1 mt-6"
						item-key="correlationId" hover color="primaryMoe" :items-per-page="itemsPerPage"
						:sort-by="[{ key: 'lastModified', order: 'desc' }]" 
						:footer-props="{
							itemsPerPageOptions: [5, 10, 25, 50, -1],
							itemsPerPageText: 'Documents per page:',
							showFirstLastPage: true
						}">

						<!-- eslint-disable vue/valid-v-slot -->
						<template #item.createdOn="{ item }">
							{{ moment.utc(item.createdOn).local().format('MM/DD/YYYY h:mm:ss A') }}
						</template>

						<!-- eslint-disable vue/valid-v-slot -->
						<template #item.lastModified="{ item }">
							{{ moment.utc(item.lastModified).local().format('MM/DD/YYYY h:mm:ss A') }}
						</template>

						<!-- eslint-disable vue/valid-v-slot -->
						<template #item.download="{ item }">
							<v-icon color="primary" @click="downloadFile(item.docUrl)" style="cursor: pointer;">
								mdi-download
							</v-icon>
						</template>

						<!-- eslint-disable vue/valid-v-slot -->
						<template #item.analyze="{ item }">
							<v-icon color="primary" @click="openAnalyzer(item.docUrl)" style="cursor: pointer;"
								title="Open in Trace Worksheet Analyzer">
								mdi-file-search-outline
							</v-icon>
						</template>

					</v-data-table>
				</v-container>
			</v-tabs-window-item>

		</v-tabs-window>
	</v-container>
</template>

<script lang="ts">
import { ref, computed } from 'vue';
import { useRoute } from 'vue-router';
import WaitDialog from '@/components/WaitDialog.vue';
import UserNotification from '@/components/UserNotification.vue';
import TraceWorksheetAnalysis from '@/components/TraceWorksheetAnalysis.vue';
import { DataTableHeader } from '@/models/DataTableHeader';
import { usePolicyDocumentsLogic } from '@/composables/PolicyDocuments/UsePolicyDocumentsLogic';
import moment from 'moment';


export default {
	name: 'ViewPolicyDocuments',
	route: {
		path: '/policy-documents',
		meta: {
			requiresAuth: true,
			title: 'Policy Documents'
		}
	},
	components: {
		WaitDialog,
		UserNotification,
		TraceWorksheetAnalysis
	},
	setup() {
		const route = useRoute();
		const policyNumber = ref<string | null>(
			(route.query.policyNumber as string) || null
		);

		const filter = ref<string>('traceworksheets');

		// Tab management
		const tab = ref<string>("1");
		const itemsPerPage = ref<number>(10);

		// Use the composable for data management and error handling
		const {
			policyDocs,
			errorMessage,
			isLoading
		} = usePolicyDocumentsLogic(policyNumber, filter);

		const downloadFile = (url: string) => {
			window.location.href = url;
		};

		// Trace Worksheet Analyzer slide-up state
		const analyzerActive = ref<boolean>(false);
		const analyzerDocUrl = ref<string>('');

		const openAnalyzer = (docUrl: string) => {
			analyzerDocUrl.value = docUrl;
			analyzerActive.value = true;
		};

		const analyzerClosed = () => {
			analyzerActive.value = false;
		};

		// Computed property for waiting state
		const isWaiting = computed(() => isLoading.value);

		const headers = ref<DataTableHeader[]>([
			{ title: 'Last Modified', key: 'lastModified', align: 'start' },
			{ title: 'File Name', key: 'fileName', align: 'start' },
			{ title: 'Content Type', key: 'contentType', align: 'start' },
			{ title: 'Created On', key: 'createdOn', align: 'start' },
			{ title: 'Download', key: 'download', align: 'center', sortable: false },
			{ title: 'Analyze', key: 'analyze', align: 'center', sortable: false }
		]);

		return {
			isWaiting,
			downloadFile,
			openAnalyzer,
			analyzerActive,
			analyzerDocUrl,
			analyzerClosed,
			policyNumber,
			tab,
			policyDocs,
			errorMessage,
			itemsPerPage,
			headers,
			moment
		};
	}
};
</script>

